import { PaginatedResponse, useApi } from '@/api';
import { Asset, TableCellBottom } from '@/components';
import {
  Box,
  CurrencyValue,
  DataTable,
  Flex,
  NetworkIcon,
  PercentageValue,
  ProtocolIcon,
  RelativeDateTime,
  TableSkeleton,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import _ from 'lodash';
import { Link } from 'react-router';

type AssetData = {
  address: string;
  datetime: string;
  wallet_address: string;
  order_index: string;
  assets: string;
  assets_change: string;
  assets_usd: string;
  assets_usd_change: string;
  symbol: string;
  name: string;
  network: string;
  protocol: string;
  apy: string;
  estimated_revenue_usd: string;
  rate: string;
  profit_estimated: string;
  asset_address: string;
  asset_symbol: string;
  asset_name: string;
  type: string;
  token_type: string;
  aum: number;
  aum_change: number;
  real_rate: number;
  real_profit_estimated: number;
};

export default function AllocatedTable({
  network = null,
  showEmpty = '0'
}: {
  network?: string | null;
  showEmpty?: string;
}) {
  const { pagination, sorting } = useTableProps({
    pageSize: 15,
    initialSorting: '-assets'
  });
  const [data, error] = useApi<PaginatedResponse<AssetData[]>>({
    url: '/sparkstar/sll/assets/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      token_type: 'allocation',
      show_empty: showEmpty,
      network
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn<AssetData>[] = [
    {
      header: 'Network',
      getCell: (row) => (
        <Flex gap="xs" alignItems="center">
          <NetworkIcon network={row.network} size="2" />
          {_.capitalize(row.network)}
        </Flex>
      ),
      sortingField: 'network'
    },
    {
      header: 'Protocol',
      getCell: (row) =>
        row.protocol === null ? (
          '-'
        ) : (
          <Flex gap="xs" alignItems="center">
            <ProtocolIcon protocol={row.protocol} size="2" />
            {_.capitalize(row.protocol)}
          </Flex>
        ),
      sortingField: 'protocol'
    },
    {
      header: 'Underying Asset',
      getCell: (row) => (
        <>
          {row.asset_address ? (
            <Asset
              symbol={row.asset_symbol}
              address={row.asset_address}
              network={row.network}
              iconSize="2"
              showName={false}
            />
          ) : (
            '-'
          )}
        </>
      ),
      sortingField: 'asset_symbol'
    },
    {
      header: 'Asset',
      getCell: (row) => (
        <Flex flexDirection="column" gap="4xs">
          <Box as="span">{row.symbol}</Box>
          <Box as="span" color="muted" fontSize="-2">
            {row.name}
          </Box>
        </Flex>
      ),
      sortingField: 'symbol',
      sticky: true
    },
    {
      header: 'Total Value',
      getCell: (row) => (
        <CurrencyValue value={row.assets} options={{ currency: 'USD' }} />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.assets_change}>
          <CurrencyValue
            value={row.assets_change}
            options={{ currency: 'USD', trend: true, trendIconSize: '-2' }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'assets'
    },

    {
      header: 'Allocated Value',
      getCell: (row) => (
        <CurrencyValue value={row.aum} options={{ currency: 'USD' }} />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.aum_change}>
          <CurrencyValue
            value={row.aum_change}
            options={{ currency: 'USD', trend: true, trendIconSize: '-2' }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'aum'
    },
    {
      header: 'APY',
      getCell: (row) => (
        <>
          {Number(row.real_rate) > 0 ? (
            <PercentageValue value={row.rate} />
          ) : (
            '-'
          )}
        </>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'rate'
    },

    {
      header: 'Estimated Revenue',
      getCell: (row) => (
        <>
          {Number(row.profit_estimated) > 0 ? (
            <CurrencyValue
              value={row.profit_estimated}
              options={{
                currency: 'USD',
                absolute: true
              }}
            />
          ) : (
            '-'
          )}
        </>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'profit_estimated'
    },

    {
      header: 'Latest Activity',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];

  if (data.results.length === 0) {
    return null;
  }

  return (
    <>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        rowLinkProps={{
          to: (row) =>
            `/spark-liquidity-layer/${row.network}/${row.wallet_address}/${row.address}`,
          // @ts-ignore
          linkComponent: Link
        }}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    </>
  );
}
